import React, { useEffect, useState } from "react";
import { ClipboardList, Plus, Trash2, GripVertical, Loader2, Save, ChevronUp, ChevronDown, X } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { PageHeader, SectionCard, EmptyState, Loading } from "@/components/common";
import { Btn, TextInput, Textarea, Select, DataTable } from "@/components/DataTable";
import { Field } from "@/components/common";
import { Modal } from "@/components/Modal";

const FIELD_TYPES = [
  ["short_text", "Short text"], ["long_text", "Long text"], ["number", "Number"],
  ["dropdown", "Dropdown"], ["multiselect", "Multi-select"], ["checkbox", "Checkbox"],
  ["date", "Date"], ["yesno", "Yes / No"], ["passfail", "Pass / Fail"], ["photo", "Photo"],
];
const HAS_OPTIONS = ["dropdown", "multiselect"];
const uid = () => "fld_" + Math.random().toString(36).slice(2, 9);

export default function Forms() {
  const [rows, setRows] = useState([]);
  const [services, setServices] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // form object or null

  const load = async () => {
    setLoading(true);
    try {
      const [f, s, a] = await Promise.all([api.get("/forms"), api.get("/service-types"), api.get("/assignees")]);
      setRows(f.data); setServices(s.data); setAssignees(a.data);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const newForm = () => setEditing({ title: "", description: "", service_type_ids: [], active: true, fields: [], raise_on_fail: false, fail_owner_id: "", fail_priority: "High", fail_due_days: 7 });
  const edit = (r) => setEditing(JSON.parse(JSON.stringify(r)));
  const del = async (r) => { if (!window.confirm(`Delete "${r.title}"? Existing submissions will be removed.`)) return; await api.delete(`/forms/${r.id}`); load(); };

  const svcName = (id) => services.find((s) => s.id === id)?.name || id;

  const columns = [
    { key: "title", label: "Form", render: (r) => <div><button onClick={() => edit(r)} className="font-medium text-slate-700 hover:text-seq-green text-left">{r.title}</button><div className="text-xs text-slate-400">{r.fields.length} field{r.fields.length !== 1 ? "s" : ""}</div></div> },
    { key: "service_type_ids", label: "Applies to", render: (r) => r.service_type_ids.length ? <div className="flex flex-wrap gap-1">{r.service_type_ids.map((id) => <span key={id} className="text-xs bg-seq-green/10 text-seq-green px-2 py-0.5 rounded-full">{svcName(id)}</span>)}</div> : <span className="text-xs text-slate-400">Not linked — won't appear on jobs</span> },
    { key: "active", label: "Status", render: (r) => <span className={`text-xs px-2 py-0.5 rounded-full ${r.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{r.active ? "Active" : "Inactive"}</span> },
    { key: "created_at", label: "Created", render: (r) => <span className="text-xs text-slate-400">{fmtDate(r.created_at)}</span> },
    { key: "actions", label: "", align: "right", render: (r) => <button onClick={() => del(r)} data-testid={`delete-form-${r.id}`} className="p-1.5 text-slate-400 hover:text-red-500"><Trash2 size={15} /></button> },
  ];

  return (
    <div>
      <PageHeader title="Custom Forms" subtitle="Build site-audit and risk-assessment forms that consultants complete on the job" icon={ClipboardList}
        actions={<Btn onClick={newForm} data-testid="new-form-btn"><Plus size={16} /> New Form</Btn>} />
      <SectionCard noPad>
        {loading ? <Loading /> : <DataTable columns={columns} rows={rows} empty={<EmptyState icon={ClipboardList} title="No forms yet" hint="Create a form and link it to a service type so it appears when consultants complete matching jobs." />} />}
      </SectionCard>
      {editing && <FormEditor form={editing} services={services} assignees={assignees} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function FormEditor({ form, services, assignees, onClose, onSaved }) {
  const [f, setF] = useState(form);
  const [saving, setSaving] = useState(false);
  const hasPassFail = f.fields.some((fl) => fl.type === "passfail");

  const setField = (i, patch) => setF((s) => ({ ...s, fields: s.fields.map((fl, j) => (j === i ? { ...fl, ...patch } : fl)) }));
  const addField = () => setF((s) => ({ ...s, fields: [...s.fields, { id: uid(), label: "", type: "short_text", required: false, options: [], help: "" }] }));
  const removeField = (i) => setF((s) => ({ ...s, fields: s.fields.filter((_, j) => j !== i) }));
  const move = (i, dir) => setF((s) => {
    const arr = [...s.fields]; const j = i + dir;
    if (j < 0 || j >= arr.length) return s;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    return { ...s, fields: arr };
  });
  const toggleSvc = (id) => setF((s) => ({ ...s, service_type_ids: s.service_type_ids.includes(id) ? s.service_type_ids.filter((x) => x !== id) : [...s.service_type_ids, id] }));

  const save = async () => {
    if (!f.title.trim()) return toast.error("Give the form a title");
    if (!f.fields.length) return toast.error("Add at least one field");
    if (f.fields.some((fl) => !fl.label.trim())) return toast.error("Every field needs a label");
    if (f.raise_on_fail && !f.fail_owner_id) return toast.error("Choose who failed checks should be assigned to");
    setSaving(true);
    try {
      const payload = { ...f, fields: f.fields.map((fl) => ({ ...fl, options: HAS_OPTIONS.includes(fl.type) ? (fl.options || []).filter(Boolean) : [] })) };
      if (f.id) await api.put(`/forms/${f.id}`, payload);
      else await api.post("/forms", payload);
      toast.success("Form saved");
      onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <Modal open onOpenChange={onClose} wide title={f.id ? "Edit form" : "New form"} testId="form-editor-modal">
      <div className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Form title *"><TextInput value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="e.g. Site Audit" data-testid="form-title" /></Field>
          <Field label="Active"><label className="flex items-center gap-2 text-sm text-slate-600 mt-2"><input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} className="rounded" data-testid="form-active" /> Show this form on matching jobs</label></Field>
        </div>
        <Field label="Description"><Textarea rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="Optional guidance for the consultant" data-testid="form-description" /></Field>

        <div>
          <div className="text-sm font-medium text-slate-700 mb-1.5">Appears for these service types</div>
          <div className="flex flex-wrap gap-2" data-testid="form-service-types">
            {services.length === 0 && <span className="text-xs text-slate-400">No service types configured yet (add them in Settings).</span>}
            {services.map((s) => (
              <button key={s.id} type="button" onClick={() => toggleSvc(s.id)} data-testid={`form-svc-${s.id}`}
                className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${f.service_type_ids.includes(s.id) ? "bg-seq-green text-white border-seq-green" : "bg-white text-slate-600 border-slate-200 hover:border-seq-green"}`}>{s.name}</button>
            ))}
          </div>
        </div>

        <div className="border-t border-slate-100 pt-3">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium text-slate-700">Fields</div>
            <Btn variant="secondary" onClick={addField} data-testid="add-field-btn"><Plus size={14} /> Add field</Btn>
          </div>
          <div className="space-y-3">
            {f.fields.length === 0 && <p className="text-sm text-slate-400">No fields yet — add your first question.</p>}
            {f.fields.map((fl, i) => (
              <div key={fl.id} className="rounded-lg border border-slate-200 p-3 bg-slate-50/50" data-testid={`field-row-${i}`}>
                <div className="flex items-start gap-2">
                  <div className="flex flex-col gap-0.5 pt-1 text-slate-300">
                    <button type="button" onClick={() => move(i, -1)} className="hover:text-slate-500"><ChevronUp size={15} /></button>
                    <button type="button" onClick={() => move(i, 1)} className="hover:text-slate-500"><ChevronDown size={15} /></button>
                  </div>
                  <div className="flex-1 space-y-2">
                    <div className="grid sm:grid-cols-2 gap-2">
                      <TextInput value={fl.label} onChange={(e) => setField(i, { label: e.target.value })} placeholder="Question / label" data-testid={`field-label-${i}`} />
                      <Select value={fl.type} onChange={(e) => setField(i, { type: e.target.value })} data-testid={`field-type-${i}`}>
                        {FIELD_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </Select>
                    </div>
                    {HAS_OPTIONS.includes(fl.type) && (
                      <TextInput value={(fl.options || []).join(", ")} onChange={(e) => setField(i, { options: e.target.value.split(",").map((x) => x.trim()) })} placeholder="Options, comma-separated (e.g. Low, Medium, High)" data-testid={`field-options-${i}`} />
                    )}
                    <div className="flex items-center gap-3">
                      <label className="flex items-center gap-1.5 text-xs text-slate-500"><input type="checkbox" checked={fl.required} onChange={(e) => setField(i, { required: e.target.checked })} className="rounded" data-testid={`field-required-${i}`} /> Required</label>
                    </div>
                  </div>
                  <button type="button" onClick={() => removeField(i)} className="text-slate-300 hover:text-red-500 pt-1" data-testid={`remove-field-${i}`}><X size={16} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="border-t border-slate-100 pt-3" data-testid="fail-followup-section">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-medium text-slate-700">Failed checks → follow-up</div>
              <p className="text-xs text-slate-400">When a Pass/Fail answer is marked <span className="font-medium text-red-500">Fail</span>, auto-raise a follow-up action assigned to an owner.</p>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-600 shrink-0">
              <input type="checkbox" checked={!!f.raise_on_fail} onChange={(e) => setF({ ...f, raise_on_fail: e.target.checked })} className="rounded" data-testid="fail-raise-toggle" />
              Enable
            </label>
          </div>
          {f.raise_on_fail && (
            <>
              {!hasPassFail && <p className="mt-2 text-xs text-amber-600">Tip: add at least one Pass/Fail field above for this to trigger.</p>}
              <div className="grid sm:grid-cols-3 gap-3 mt-3">
                <Field label="Assign follow-up to *">
                  <Select value={f.fail_owner_id || ""} onChange={(e) => setF({ ...f, fail_owner_id: e.target.value })} data-testid="fail-owner-select">
                    <option value="">Select owner…</option>
                    {assignees.map((u) => <option key={u.id} value={u.id}>{u.name}{u.role_name ? ` — ${u.role_name}` : ""}</option>)}
                  </Select>
                </Field>
                <Field label="Priority">
                  <Select value={f.fail_priority || "High"} onChange={(e) => setF({ ...f, fail_priority: e.target.value })} data-testid="fail-priority-select">
                    {["Critical", "High", "Normal", "Low"].map((p) => <option key={p}>{p}</option>)}
                  </Select>
                </Field>
                <Field label="Due in (days)">
                  <TextInput type="number" min="0" value={f.fail_due_days ?? 7} onChange={(e) => setF({ ...f, fail_due_days: e.target.value })} data-testid="fail-due-days" />
                </Field>
              </div>
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-100 pt-3">
          <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
          <Btn onClick={save} disabled={saving} data-testid="save-form-btn">{saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save form</Btn>
        </div>
      </div>
    </Modal>
  );
}
